import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  createRelease,
  deleteReleaseChange,
  discardRelease,
  patchRelease,
  publishRelease,
  putReleaseChange,
  qrWriteMessage,
  rollbackRelease,
  type QrCodeSummary,
  type QrRelease,
  type QrReleaseChange,
} from "./qr-api";
import { isHttpsUrl, printedAddress, publishReady } from "./qr-format";
import { TextField, useQrMe, useRefreshQr } from "./QrWriteActions";

// Writes for releases (spec: os-portal specs/qr-codes-module.md V2). The Worker
// enforces every rule (draft only, one draft per code, approver, rollback
// safety); these controls only hide what the person's `/v1/qr/me` flags say
// they cannot do, and state the Worker's refusal in words.

function ErrorLine({ error }: { error: unknown }) {
  return (
    <p className="text-sm text-ks-ink" role="alert">
      {qrWriteMessage(error)}
    </p>
  );
}

/** `+ New release`: a named, empty draft. Nothing printed changes. */
export function NewReleaseButton({ onCreated }: { onCreated: (id: number) => void }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const create = useMutation({
    mutationFn: () => createRelease({ name: name.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) }),
    onSuccess: (response) => {
      void refresh();
      setOpen(false);
      setName("");
      setNotes("");
      onCreated(response.data.id);
    },
  });
  if (!me?.can_edit) return null;
  return (
    <>
      <Button onClick={() => setOpen(true)}>+ New release</Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) create.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New release</DialogTitle>
            <DialogDescription>A release is a set of destination changes that go live together. It starts as a draft, and nothing printed changes until an approver publishes it.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <TextField id="qr-release-name" label="Name" value={name} onChange={setName} placeholder="Precision app launch" />
            <div className="flex flex-col gap-2">
              <Label htmlFor="qr-release-notes">Notes (optional)</Label>
              <Textarea id="qr-release-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
            {create.isError && <ErrorLine error={create.error} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={name.trim() === "" || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Draft only, edit users: rename and notes. */
export function RenameReleaseButton({ release }: { release: QrRelease }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(release.name);
  const [notes, setNotes] = useState(release.notes ?? "");
  const save = useMutation({
    mutationFn: () => patchRelease(release.id, { name: name.trim(), notes: notes.trim() }),
    onSuccess: () => {
      void refresh();
      setOpen(false);
    },
  });
  if (!me?.can_edit || release.status !== "draft") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>
        Rename…
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) save.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename release</DialogTitle>
            <DialogDescription>Only a draft release can be renamed.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <TextField id="qr-release-rename" label="Name" value={name} onChange={setName} />
            <div className="flex flex-col gap-2">
              <Label htmlFor="qr-release-rename-notes">Notes</Label>
              <Textarea id="qr-release-rename-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
            {save.isError && <ErrorLine error={save.error} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={name.trim() === "" || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * `Add a code` (a code that is in no other draft release) or, with `existing`,
 * change that row's new destination. https only.
 */
export function ChangeRowButton({
  release,
  codes,
  taken,
  existing,
}: {
  release: QrRelease;
  codes: QrCodeSummary[];
  /** Code ids already in another draft release. */
  taken: ReadonlySet<string>;
  existing?: QrReleaseChange;
}) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const [codeId, setCodeId] = useState(existing?.code_id ?? "");
  const [url, setUrl] = useState(existing?.target_url ?? "");
  const inRelease = new Set(release.changes.map((change) => change.code_id));
  const choices = codes.filter((code) => code.status !== "retired" && !taken.has(code.id) && !inRelease.has(code.id));
  const save = useMutation({
    mutationFn: () => putReleaseChange(release.id, codeId, url.trim()),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      if (!existing) {
        setCodeId("");
        setUrl("");
      }
    },
  });
  if (!me?.can_edit || release.status !== "draft") return null;
  const next = url.trim();
  const ready = codeId !== "" && isHttpsUrl(next) && next !== existing?.target_url;
  return (
    <>
      <Button size="sm" variant={existing ? "ghost" : "outline"} className="text-foreground" onClick={() => setOpen(true)}>
        {existing ? "Edit" : "Add a code"}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) save.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{existing ? `Change where ${existing.label} goes after publish` : "Add a code to this release"}</DialogTitle>
            <DialogDescription>Nothing changes for anyone who scans until an approver publishes the release.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            {!existing && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="qr-release-code">Code</Label>
                <Select value={codeId} onValueChange={setCodeId}>
                  <SelectTrigger id="qr-release-code" className="w-full">
                    <SelectValue placeholder="Choose a code…" />
                  </SelectTrigger>
                  <SelectContent>
                    {choices.map((code) => (
                      <SelectItem key={code.id} value={code.id}>
                        {code.label} ({printedAddress(code.printed_url)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {choices.length === 0 && <p className="text-sm text-muted-foreground">Every code is already in this release or in another draft release.</p>}
              </div>
            )}
            <TextField id="qr-release-target" label="New destination (https address)" value={url} onChange={setUrl} placeholder="https://" />
            {save.isError && <ErrorLine error={save.error} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : existing ? "Save change" : "Add to release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RemoveChangeButton({ release, change }: { release: QrRelease; change: QrReleaseChange }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const remove = useMutation({
    mutationFn: () => deleteReleaseChange(release.id, change.code_id),
    onSuccess: () => void refresh(),
  });
  if (!me?.can_edit || release.status !== "draft") return null;
  return (
    <>
      <Button size="sm" variant="ghost" className="text-foreground" disabled={remove.isPending} onClick={() => remove.mutate()}>
        Remove
      </Button>
      {remove.isError && <ErrorLine error={remove.error} />}
    </>
  );
}

/** Draft only, edit users. Nothing on a code changes. */
export function DiscardReleaseButton({ release, onDone }: { release: QrRelease; onDone: () => void }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const discard = useMutation({
    mutationFn: () => discardRelease(release.id),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      onDone();
    },
  });
  if (!me?.can_edit || release.status !== "draft") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>
        Discard
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) discard.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard {release.name}?</DialogTitle>
            <DialogDescription>The draft is closed and its test links stop working. No code changes.</DialogDescription>
          </DialogHeader>
          {discard.isError && <ErrorLine error={discard.error} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Keep draft</Button>
            <Button variant="destructive" disabled={discard.isPending} onClick={() => discard.mutate()}>
              {discard.isPending ? "Discarding…" : "Discard release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Approver only, a draft with at least one change. The release name is typed to confirm. */
export function PublishButton({ release }: { release: QrRelease }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const publish = useMutation({
    mutationFn: () => publishRelease(release.id),
    onSuccess: () => {
      void refresh();
      setOpen(false);
      setTyped("");
    },
  });
  if (!me?.can_approve || release.status !== "draft" || release.changes.length === 0) return null;
  const ready = publishReady({ name: release.name, typed, changeCount: release.changes.length });
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Publish…
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) publish.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish {release.name}?</DialogTitle>
            <DialogDescription>
              Everyone who scans these printed codes from now on lands on the new destinations. It takes up to a minute.
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-2 text-sm text-ks-ink">
            {release.changes.map((change) => (
              <li key={change.code_id} className="break-all">
                <span className="font-medium">{change.label}</span>: {change.current_target_url} → {change.target_url}
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2">
            <Label htmlFor="qr-release-confirm">Type the release name ({release.name}) to confirm.</Label>
            <Input id="qr-release-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={release.name} />
          </div>
          {publish.isError && <ErrorLine error={publish.error} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!ready || publish.isPending} onClick={() => publish.mutate()}>
              {publish.isPending ? "Publishing…" : "Publish release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Approver only, a published release. The Worker refuses if a code changed since (409 changed_since). */
export function RollbackButton({ release }: { release: QrRelease }) {
  const me = useQrMe();
  const refresh = useRefreshQr();
  const [open, setOpen] = useState(false);
  const rollback = useMutation({
    mutationFn: () => rollbackRelease(release.id),
    onSuccess: () => {
      void refresh();
      setOpen(false);
    },
  });
  if (!me?.can_approve || release.status !== "published") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="text-foreground" onClick={() => setOpen(true)}>
        Roll back…
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) rollback.reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Roll back {release.name}?</DialogTitle>
            <DialogDescription>Each code goes back to where it pointed before this release. It takes up to a minute.</DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-2 text-sm text-ks-ink">
            {release.changes.map((change) => (
              <li key={change.code_id} className="break-all">
                <span className="font-medium">{change.label}</span> goes back to {change.previous_target_url ?? "its earlier destination"}
              </li>
            ))}
          </ul>
          {rollback.isError && <ErrorLine error={rollback.error} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={rollback.isPending} onClick={() => rollback.mutate()}>
              {rollback.isPending ? "Rolling back…" : "Roll back release"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
